import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';
import { matchesUrl, pubUrl, searchUrl } from '@findemdev/findem-helpers';
import {
  ICompanyListsResponse, ICreateCompanyListResponse, ICompAutoCompleteParams, ICompAutoCompleteResponse, ISearchCompanyParams, ISearchCompanyResponse, IGetCompanyListByRefsResponse,
  IUpdateCompanyListPayload, IUpdateCompanyListShortlistPayload, IUpdateCompanyListRequirementsPayload, IShareWithOrgPayload, IGetAllCompanyListsPayload,
  IDealsParams,
  IDealsResponse,
  ICreateSyncedCompanyListsPayload, ICreateSyncedCompanyListsResponse
} from '../components/CompanyBuilder/types/api';
import { IRequirement } from '../types/searchRequest';
import { ICompanyList } from '../components/CompanyBuilder/types/common';
import { VendorType } from '../components/CompanyBuilder/common/constants';
import { ISimilarCompany, ISuggestedCompanyParams } from '../types/search';
import { ICompanyInfoResponse, ICompanyListParams, ICompanyOptions } from '../components/CompanyBuilder/types/companyList';
import { APP_NAMES } from '../types/app';

export const companyApi = createApi({
  reducerPath: 'company',
  tagTypes: ['CompanyLists', 'CompanyListByRef'],
  baseQuery: fetchBaseQuery({ baseUrl: matchesUrl }),
  endpoints: (build) => ({
    getCompany: build.query({
      query: (id) => `/company/${id}`,
    }),
    createCompany: build.mutation({
      query: (company) => ({
        url: '/company',
        method: 'POST',
        body: company,
        credentials: 'include',
      }),
    }),
    searchCompany: build.query<ISearchCompanyResponse, ISearchCompanyParams>({
      query: (query: ISearchCompanyParams) => ({
        url: `/hm/api/compbuilder/company/search?${new URLSearchParams({ joid: query.joid }).toString()}`,
        method: 'POST',
        credentials: 'include',
        body: {
          joid: query.joid,
          page: query.page,
          size: query.size,
          vendor: query.vendor,
          requirements: query.requirements,
          shortlist: query.shortlist,
          company_ids: query.company_ids,
          search_id: query.search_id,
        },
      }),
    }),
    searchCompanyLists: build.query<ICompanyListsResponse, IGetAllCompanyListsPayload>({
      providesTags: ['CompanyLists'],
      query: ({ page, size, filters, joid, sort_fields }) => {
        const searchParams = new URLSearchParams({
          joid,
        });
        return {
          url: `hm/api/compbuilder/search?${searchParams.toString()}`,
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: {
            page: page,
            size: size,
            ...(filters && { filters }),
            ...(sort_fields && { sort_fields }),
          }
        };
      }
    }),
    shareWithOrg: build.mutation<ICompanyList, IShareWithOrgPayload>({
      invalidatesTags: ['CompanyLists'],
      query: (params) => {
        const searchParams = new URLSearchParams({
          joid: params.joid,
        });

        return {
          url: `hm/api/compbuilder/share_with_org?${searchParams.toString()}`,
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: {
            ref: params.ref,
          }
        };
      }
    }),
    createNewCompanyList: build.mutation<ICreateCompanyListResponse, {
      joid: string,
      vendor: VendorType,
      appName?: string,
    }>({
      invalidatesTags: ['CompanyLists'],
      query: (params) => {
        const searchParams = new URLSearchParams({
          joid: params.joid,
        });

        const currentDate = new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });

        const defaultName = params.appName === APP_NAMES.RRA
          ? `TAR List (${currentDate})`
          : `Company Research List (${currentDate})`;

        return {
          url: `hm/api/compbuilder?${searchParams.toString()}`,
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: {
            name: defaultName,
            vendor: params.vendor,
            requirements: [],
            collaborators: [],
            shortlist: [],
          }
        };
      },
    }),
    updateCompanyList: build.mutation<ICreateCompanyListResponse, IUpdateCompanyListPayload>({
      invalidatesTags: ['CompanyLists'],
      query: (params) => {
        const searchParams = new URLSearchParams({
          joid: params.joid,
        });

        return {
          url: `hm/api/compbuilder?${searchParams.toString()}`,
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: params.companyList,
        };
      }
    }),
    updateCompanyListShortlist: build.mutation<ICreateCompanyListResponse, IUpdateCompanyListShortlistPayload>({
      invalidatesTags: ['CompanyLists'],
      query: (params) => {
        const searchParams = new URLSearchParams({
          joid: params.joid,
        });

        return {
          url: `hm/api/compbuilder?${searchParams.toString()}`,
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: {
            joid: params.joid,
            ref: params.ref,
            vendor: params.vendor,
            shortlist: params.shortlist,
          },
        };
      }
    }),
    updateCompanyListRequirements: build.mutation<ICreateCompanyListResponse, IUpdateCompanyListRequirementsPayload>({
      invalidatesTags: ['CompanyLists'],
      query: (params) => {
        const searchParams = new URLSearchParams({
          joid: params.joid,
        });

        return {
          url: `hm/api/compbuilder?${searchParams.toString()}`,
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: {
            joid: params.joid,
            ref: params.ref,
            vendor: params.vendor,
            requirements: params.requirements,
          },
        };
      }
    }),
    deleteCompanyList: build.mutation<any, {
      joid: string,
      ref: string,
    }>({
      invalidatesTags: ['CompanyLists'],
      query: (params) => {
        const searchParams = new URLSearchParams({
          joid: params.joid,
        });

        return {
          url: `hm/api/compbuilder?${searchParams.toString()}`,
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: {
            ref: params.ref,
          }
        }
      }
    }),
    compAutoComplete: build.query<ICompAutoCompleteResponse, ICompAutoCompleteParams>({
      query: (query: ICompAutoCompleteParams) => ({
        url: '/hm/api/compbuilder/company/autocomplete',
        method: 'GET',
        credentials: 'include',
        params: query,
      }),
    }),
    getCompanyListByRefs: build.query<IGetCompanyListByRefsResponse, {
      joid: string,
      refs: string[],
    }>({
      query: (params) => {
        const searchParams = new URLSearchParams({
          joid: params.joid,
        });

        // adding refs here instead of in URLSearchParams as to avoid decoding multiple refs caused by comma
        let additionalParams = '';

        additionalParams += params.refs.length ? `&ref=${params.refs.join(",")}` : '';

        return {
          url: `hm/api/compbuilder?${searchParams.toString()}${additionalParams}`,
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
        };
      },
      transformResponse: (response: IGetCompanyListByRefsResponse) => {
        response.data.forEach((company: ICompanyList) => {
          company.requirements = company?.requirements?.map((requirement: IRequirement) => {
            requirement.bool_str = unescape(requirement.bool_str);
            return requirement;
          }) ?? [];
        });
        return response;
      },
    }),
    getCompanyDeals: build.query<IDealsResponse, IDealsParams>({
      query: (params: IDealsParams) => {
        const _params: Record<string, string> = {
          joid: params.joid,
          investor_id: params.investor_id,
          page: params.page.toString(),
          page_size: params.page_size.toString(),
        };
        if (params.active !== undefined) {
          _params.active = params.active;
        }
        const searchParams = new URLSearchParams(_params);

        return {
          url: `${pubUrl}/pub/api/rra_pitchbook/deals?${searchParams.toString()}`,
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
        };
      },
    }),
    getSimilarCompanies: build.query<
      ISimilarCompany[],
      ISuggestedCompanyParams
    >({
      query: (params: ISuggestedCompanyParams) => ({
        url: `${searchUrl}/pub/api/simcomps?incl_size=true&count=50&${params.key ? params.key : "cnames"
          }=${params.values.join("%2C")}`,
        credentials: "include",
        method: "GET",
      }),
    }),
    companyListByName: build.query<ICompanyInfoResponse, ICompanyListParams>({
      query: (params: ICompanyListParams) => ({
        url: `${pubUrl}/pub/api/company/resolve?${new URLSearchParams({ joid: params.joId }).toString()}`,
        credentials: 'include',
        method: 'POST',
        body: {
          joid: params.joId,
          companies_info: params.companyNames
        }
      }),
      transformResponse: (response: ICompanyInfoResponse, _meta, _arg) => response,
    }),
    getCopilotAICompanyList: build.mutation<string[], { text: string; minCount?: number; maxCount?: number }>({
      query: ({ text, minCount, maxCount }) => ({
        url: `${searchUrl}/pub/api/copilot-company-list`,
        credentials: "include",
        method: "POST",
        body: {
          company_list_info: {
            company_list: text,
            min_count: minCount,
            max_count: maxCount,
          },
        },
      }),
      transformResponse: (response: { company_list_response: { company_list: string[] } }) =>
        response.company_list_response.company_list,
    }),
    fetchCompanyOptions: build.mutation<ICompanyOptions[], { prefix: string; count?: number }>({
      query: ({ prefix, count = 15 }) => ({
        url: `${searchUrl}/pub/api/company_ac`,
        method: 'POST',
        body: { prefix, count },
        credentials: 'include',
      }),
    }),
    createSyncedCompanyLists: build.mutation<ICreateSyncedCompanyListsResponse, ICreateSyncedCompanyListsPayload>({
      query: (params) => ({
        url: `hm/api/compbuilder/create_synced_company_lists`,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: params,
      }),
    }),
  }),
});

export const {
  useGetCompanyQuery,
  useCreateCompanyMutation,
  useSearchCompanyQuery,
  useLazySearchCompanyQuery,
  useShareWithOrgMutation,
  useSearchCompanyListsQuery,
  useCreateNewCompanyListMutation,
  useDeleteCompanyListMutation,
  useCompAutoCompleteQuery,
  useGetCompanyListByRefsQuery,
  useUpdateCompanyListMutation,
  useUpdateCompanyListShortlistMutation,
  useUpdateCompanyListRequirementsMutation,
  useGetCompanyDealsQuery,
  useGetSimilarCompaniesQuery,
  useCompanyListByNameQuery,
  useGetCopilotAICompanyListMutation,
  useFetchCompanyOptionsMutation,
  useCreateSyncedCompanyListsMutation,
} = companyApi;
